/**
 * 게스트(외부 시연용 magic URL) 로그인에 사용되는 AES-GCM 암복호화 유틸.
 * URL 쿼리로 전달받은 암호화된 ID/PW를 복호화하는 용도로 사용하며,
 * encryptWithAES는 개발 환경에서 testUtils를 통해 콘솔 테스트용으로 노출된다.
 */
class GuestLoginService {
  // Uint8Array → 문자열 변환
  private uint8ArrayToString = (array: Uint8Array): string => {
    return new TextDecoder().decode(array)
  }

  // Uint8Array → hex 문자열 변환
  private uint8ArrayToHex = (array: Uint8Array): string => {
    return Array.from(array)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
  }

  // 문자열 → Uint8Array 변환
  private stringToUint8Array = (str: string): Uint8Array => {
    return new TextEncoder().encode(str)
  }

  // hex 문자열 → Uint8Array 변환
  // ⚠️ WebCrypto(importKey 등)는 BufferSource(= ArrayBufferView<ArrayBuffer>)를 요구하므로
  // 반드시 `new Uint8Array(length)`로 새 ArrayBuffer를 할당해 Uint8Array<ArrayBuffer>를 보장할 것.
  // Uint8Array.from(...)은 Uint8Array<ArrayBufferLike>로 넓어져 TS 5.7+에서 타입 거부됨.
  private hexToUint8Array = (hex: string): Uint8Array => {
    const bytes = new Uint8Array(hex.length / 2)
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16)
    }
    return bytes
  }

  /**
   * AES-GCM 대칭키 복호화
   * @param encrypted - 암호화된 hex 문자열
   * @param key - 암호화 키 (hex)
   * @param iv - 초기화 벡터 (hex)
   * @returns 복호화된 평문
   */
  decryptAES = async (encrypted: string, key: string, iv: string): Promise<string> => {
    try {
      // Uint8Array(ArrayBufferView)라서 Buffer가 아님. 이름을 Bytes로 통일
      const keyBytes = this.hexToUint8Array(key)
      const ivBytes = this.hexToUint8Array(iv)
      const encryptedBytes = this.hexToUint8Array(encrypted)

      // 키 가져오기
      const cryptoKey = await crypto.subtle.importKey('raw', keyBytes, { name: 'AES-GCM' }, false, [
        'decrypt',
      ])

      // 복호화
      const decryptedBuffer = await crypto.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv: ivBytes,
        },
        cryptoKey,
        encryptedBytes,
      )

      return this.uint8ArrayToString(new Uint8Array(decryptedBuffer))
    } catch (error) {
      console.error('복호화 중 오류 발생:', error)
      throw new Error('복호화에 실패했습니다.')
    }
  }
  /**
   * AES-GCM을 사용하여 문자열을 암호화한다.
   * 개발 환경에서 testUtils(window.testUtils.encrypt)를 통해 콘솔 테스트용으로 사용.
   * @param text - 암호화할 문자열
   * @param key - 암호화 키 (hex, 생략 시 자동 생성)
   */
  encryptWithAES = async (
    text: string,
    key?: string,
  ): Promise<{
    encrypted: string
    key: string
    iv: string
  }> => {
    // 키가 없으면 32바이트 랜덤 키 생성
    const encryptionKey = key ?? this.uint8ArrayToHex(crypto.getRandomValues(new Uint8Array(32)))
    const keyBytes = this.hexToUint8Array(encryptionKey)

    // IV 생성
    const iv = crypto.getRandomValues(new Uint8Array(12))

    // 키 가져오기
    const cryptoKey = await crypto.subtle.importKey('raw', keyBytes, { name: 'AES-GCM' }, false, [
      'encrypt',
    ])

    // 암호화
    const encryptedBuffer = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      cryptoKey,
      this.stringToUint8Array(text),
    )

    return {
      encrypted: this.uint8ArrayToHex(new Uint8Array(encryptedBuffer)),
      key: encryptionKey,
      iv: this.uint8ArrayToHex(iv),
    }
  }
}

export const guestLoginService = new GuestLoginService()
